import { useEffect, useMemo, useRef, useState, type DragEvent, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Calendar,
  CalendarClock,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  ExternalLink,
  FileText,
  Hash,
  Save,
  Search,
  Send,
  Sparkles,
  Users,
  X,
} from 'lucide-react';
import { LanguageFlag, LanguageFlagByCode, type LanguageCode } from '../../components/LanguageFlag';
import { useDataroom } from '../../hooks/useDataroom';
import { dedupeStrings, type DataroomArticle } from '../../state/dataroomStore';
import { TopicTagsOverflow } from '../../components/TopicTagsOverflow';
import { dataroomApi } from '../../../api/dataroomApi';
import { getModeratorApiKey, formatModeratorApiKeyError } from '../../../utils/apiClient';
import { subscribersApi } from '../../../api/subscribersApi';
import { useToast } from '../../../contexts/ToastContext';
import { useUiLocale } from '../../../contexts/UiLocaleContext';
import {
  askChatbot,
  ensureChatSession,
  formatChatError,
  importDroppedArticle,
  readDroppedArticleId,
  setArticleDragData,
  type AttachedChatArticle,
} from './dataroomAssistantChat';
import './DataroomSettingPage.css';

type TFunc = (key: string, vars?: Record<string, string | number>) => string;

type Length = 'Brief' | 'Standard' | 'Deep';
type DeliveryMethod = 'send_now' | 'schedule_send' | 'export_file';
type ScheduleType = 'one_time' | 'recurring';
type ExportFormat = 'PDF' | 'DOCX' | 'CSV';
type PreviewDevice = 'iphone' | 'android' | 'pc';
type ChatRole = 'assistant' | 'user';
type ChatMessage = {
  id: string;
  role: ChatRole;
  text: string;
};

const ASSISTANT_WELCOME_KEY = 'dataroom.assistantWelcome';
type GeneratedPreview = {
  generatedAt: Date;
  generatedAtLabel: string;
  subject: string;
  articleCount: number;
  topics: string[];
  recipientCount: number;
};
type Subscriber = {
  id: number;
  mail: string;
  username: string;
  language?: string | null;
};

function normalizeSubscriberLanguage(value: unknown): LanguageCode {
  const code = String(value ?? 'de').toLowerCase();
  if (code === 'en' || code === 'de' || code === 'vi' || code === 'fr') return code;
  return 'de';
}
type ToastApi = {
  showSuccess: (msg: string, duration?: number) => void;
  showError: (msg: string, duration?: number) => void;
};

function defaultEmailSubject(localeTag: string, t: TFunc): string {
  return t('dataroom.defaultEmailSubject', {
    date: new Date().toLocaleDateString(localeTag),
  });
}

function defaultExportFileName(): string {
  return `hawk-digest-${new Date().toISOString().slice(0, 10)}`;
}

function extractApiError(err: unknown): string {
  return formatModeratorApiKeyError(err);
}

function isArticleNotFoundError(err: unknown): boolean {
  const status = (err as { response?: { status?: number } })?.response?.status;
  const detail = String(
    (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? '',
  ).toLowerCase();
  return status === 404 || detail.includes('article not found');
}

function isArticleNotAnalyzedError(err: unknown): boolean {
  const status = (err as { response?: { status?: number } })?.response?.status;
  const detail = String(
    (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? '',
  ).toLowerCase();
  return status === 400 || detail.includes('has not been analyzed');
}

async function syncSelectedArticlesToBackend(
  selectedArticles: DataroomArticle[],
  t: TFunc,
): Promise<{ synced: DataroomArticle[]; stale: DataroomArticle[] }> {
  await dataroomApi.clearArticles();
  const synced: DataroomArticle[] = [];
  const stale: DataroomArticle[] = [];

  for (const article of selectedArticles) {
    try {
      await dataroomApi.addArticle(article.articleId);
      synced.push(article);
    } catch (err: unknown) {
      if (isArticleNotFoundError(err)) {
        stale.push(article);
        continue;
      }
      if (isArticleNotAnalyzedError(err)) {
        throw new Error(t('dataroom.errArticleNotAnalyzed', { title: article.title }));
      }
      const reason = extractApiError(err);
      throw new Error(t('dataroom.errSyncArticle', {
        title: article.title,
        id: article.articleId,
        reason,
      }));
    }
  }

  return { synced, stale };
}

async function findStaleArticlesOnServer(
  articles: DataroomArticle[],
): Promise<DataroomArticle[]> {
  const stale: DataroomArticle[] = [];
  for (const article of articles) {
    try {
      await dataroomApi.addArticle(article.articleId);
    } catch (err: unknown) {
      if (isArticleNotFoundError(err)) stale.push(article);
    }
  }
  return stale;
}

async function dispatchAndVerify(
  payload: { channel: 'email' | 'schedule'; subscriber_id: number; scheduled_for?: string },
  t: TFunc,
): Promise<void> {
  const res = await dataroomApi.dispatch(payload);
  const historyId = res.data?.history_id;
  if (!historyId) {
    throw new Error(t('dataroom.errDispatchNoHistory'));
  }

  const historyRes = await dataroomApi.getHistory(historyId);
  const status = historyRes.data?.status;
  const errorMessage = historyRes.data?.error_message;

  if (status === 'failed') {
    throw new Error(errorMessage || t('dataroom.errEmailDeliveryFailed'));
  }

  if (payload.channel === 'email' && status !== 'sent') {
    throw new Error(errorMessage || t('dataroom.errDispatchStatus', { status: status ?? 'unknown' }));
  }
}

function formatGeneratedTime(dt: Date, localeTag: string): string {
  return dt.toLocaleString(localeTag, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const DELIVERY_METHOD_CONFIG: {
  id: DeliveryMethod;
  labelKey: string;
  descKey: string;
  icon: typeof Send;
  iconBg: string;
  iconColor: string;
}[] = [
  { id: 'send_now', labelKey: 'dataroom.sendNow', descKey: 'dataroom.sendNowDesc', icon: Send, iconBg: '#2563eb', iconColor: '#fff' },
  { id: 'schedule_send', labelKey: 'dataroom.scheduleSend', descKey: 'dataroom.scheduleSendDesc', icon: CalendarClock, iconBg: '#16a34a', iconColor: '#fff' },
  { id: 'export_file', labelKey: 'dataroom.exportFile', descKey: 'dataroom.exportFileDesc', icon: Download, iconBg: '#ef4444', iconColor: '#fff' },
];

const PREVIEW_DEVICE_CONFIG: {
  id: PreviewDevice;
  labelKey: string;
  width: number;
  height: number;
}[] = [
  { id: 'iphone', labelKey: 'dataroom.deviceIphone', width: 390, height: 844 },
  { id: 'android', labelKey: 'dataroom.deviceAndroid', width: 412, height: 915 },
  { id: 'pc', labelKey: 'dataroom.devicePc', width: 1024, height: 720 },
];

const SCHEDULE_TIMEZONE_CONFIG = [
  { value: 'Europe/London', labelKey: 'campaigns.strategy.tzLondon', countryCode: 'gb' },
  { value: 'Europe/Berlin', labelKey: 'campaigns.strategy.tzBerlin', countryCode: 'de' },
  { value: 'Asia/Ho_Chi_Minh', labelKey: 'campaigns.strategy.tzHcm', countryCode: 'vn' },
  { value: 'Europe/Paris', labelKey: 'campaigns.strategy.tzParis', countryCode: 'fr' },
] as const;

const WEEKDAY_KEYS = [
  'dataroom.weekdaySu',
  'dataroom.weekdayMo',
  'dataroom.weekdayTu',
  'dataroom.weekdayWe',
  'dataroom.weekdayTh',
  'dataroom.weekdayFr',
  'dataroom.weekdaySa',
] as const;
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, '0'));
const MINUTE_OPTIONS = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

const TOPIC_TAG_ALIASES: Record<string, string> = {
  AI: 'ai',
  Finance: 'finance',
  Cloud: 'cloud',
  Blockchain: 'blockchain',
  Cybersecurity: 'cyber',
  Tech: 'tech',
  General: 'default',
  Default: 'default',
  Uncategorized: 'default',
};

const TOPIC_TAG_FALLBACKS = ['ai', 'finance', 'cloud', 'blockchain', 'cyber', 'tech', 'violet', 'emerald'] as const;

function topicTagTone(topic: string): string {
  const label = topic.trim();
  if (!label) return 'default';
  const alias = TOPIC_TAG_ALIASES[label];
  if (alias) return alias;

  const normalized = label.toLocaleLowerCase();
  const aliasKey = Object.keys(TOPIC_TAG_ALIASES).find((key) => key.toLocaleLowerCase() === normalized);
  if (aliasKey) return TOPIC_TAG_ALIASES[aliasKey]!;

  let hash = 0;
  for (let i = 0; i < normalized.length; i += 1) {
    hash = (hash + normalized.charCodeAt(i) * (i + 1)) % TOPIC_TAG_FALLBACKS.length;
  }
  return TOPIC_TAG_FALLBACKS[hash] ?? 'default';
}

function articleTopicLabels(article: DataroomArticle, t: TFunc): string[] {
  const labels = dedupeStrings(article.topics);
  return labels.length > 0 ? labels : [t('dataroom.general')];
}

function dataroomTopicTagClass(topic: string): string {
  return `ds-topic-tag ds-topic-tag--${topicTagTone(topic)} max-w-[5.5rem] truncate`;
}

function toDateKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function parseDateKey(key: string): Date | null {
  if (!key) return null;
  const [year, month, day] = key.split('-').map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateLabel(key: string, t: TFunc, localeTag: string): string {
  if (!key) return t('dataroom.selectDate');
  const parsed = parseDateKey(key);
  if (!parsed) return t('dataroom.selectDate');
  return parsed.toLocaleDateString(localeTag, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatTimeLabel(value: string, t: TFunc, localeTag: string): string {
  if (!value) return t('dataroom.selectTime');
  const [hourText, minuteText] = value.split(':');
  const hour = Number(hourText);
  if (Number.isNaN(hour) || !minuteText) return t('dataroom.selectTime');
  const date = new Date();
  date.setHours(hour, Number(minuteText), 0, 0);
  return date.toLocaleTimeString(localeTag, { hour: 'numeric', minute: '2-digit' });
}

function buildMonthCells(monthDate: Date): Array<Date | null> {
  const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const offset = monthStart.getDay();
  const daysInMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).getDate();
  const cells: Array<Date | null> = Array.from({ length: offset }, () => null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(monthDate.getFullYear(), monthDate.getMonth(), day));
  }
  return cells;
}

function isSameDay(left: Date, right: Date): boolean {
  return (
    left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate()
  );
}

function useOutsideClose(open: boolean, onClose: () => void, rootRef: RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose, rootRef]);
}

type SubscriberMultiSelectProps = {
  subscribers: Subscriber[];
  selectedIds: Set<number>;
  isLoading: boolean;
  onToggle: (id: number) => void;
  onSelectAll: () => void;
  onClear: () => void;
};

function SubscriberMultiSelect({
  subscribers,
  selectedIds,
  isLoading,
  onToggle,
  onSelectAll,
  onClear,
}: SubscriberMultiSelectProps) {
  const { t } = useUiLocale();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return subscribers;
    return subscribers.filter(
      (s) => s.mail.toLowerCase().includes(q) || s.username.toLowerCase().includes(q),
    );
  }, [subscribers, query]);

  const selectedList = useMemo(
    () => subscribers.filter((s) => selectedIds.has(s.id)),
    [subscribers, selectedIds],
  );

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="ds-field-block">
      <label className="ds-field-label">{t('dataroom.recipients')}</label>
      <div className={`ds-combobox${open ? ' ds-combobox--open' : ''}`} ref={rootRef}>
        <button
          type="button"
          className="ds-combobox-trigger"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <Search size={14} className="ds-combobox-search-icon" />
          <span className="ds-combobox-placeholder">
            {selectedIds.size > 0
              ? (selectedIds.size === 1
                ? t('dataroom.recipientsSelected', { count: selectedIds.size })
                : t('dataroom.recipientsSelectedPlural', { count: selectedIds.size }))
              : t('dataroom.searchSubscriberPlaceholder')}
          </span>
          <ChevronDown size={14} className="ds-combobox-chevron" />
        </button>

        {open && (
          <div className="ds-combobox-panel">
            <div className="ds-combobox-panel-head">
              <input
                type="text"
                className="ds-combobox-filter"
                placeholder={t('dataroom.searchByNameOrEmail')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
              />
              <div className="ds-combobox-actions">
                <button type="button" className="ds-dispatch-link" onClick={onSelectAll}>
                  {t('dataroom.selectAll')}
                </button>
                <span>·</span>
                <button type="button" className="ds-dispatch-link" onClick={onClear}>
                  {t('dataroom.clear')}
                </button>
              </div>
            </div>
            <div className="ds-combobox-list hawk-scrollbar-neon-blue">
              {isLoading ? (
                <p className="ds-dispatch-empty">{t('dataroom.loadingSubscribers')}</p>
              ) : filtered.length === 0 ? (
                <p className="ds-dispatch-empty">{t('dataroom.noSubscribersFound')}</p>
              ) : (
                filtered.map((s) => (
                  <label key={s.id} className="ds-combobox-option">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(s.id)}
                      onChange={() => onToggle(s.id)}
                    />
                    <span className="ds-combobox-option-text">
                      <span className="ds-combobox-name">
                        <LanguageFlagByCode
                          code={normalizeSubscriberLanguage(s.language)}
                          className="ds-combobox-flag"
                        />
                        {s.username || s.mail}
                      </span>
                      <span className="ds-combobox-email">{s.mail}</span>
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {selectedList.length > 0 && (
        <div className="ds-recipient-chips">
          <span className="ds-recipient-chips-label">{t('dataroom.selectedRecipients', { count: selectedList.length })}</span>
          <div className="ds-chip-row">
            {selectedList.map((s) => (
              <span key={s.id} className="ds-recipient-chip">
                <LanguageFlagByCode
                  code={normalizeSubscriberLanguage(s.language)}
                  className="ds-recipient-chip-flag"
                />
                {s.username || s.mail}
                <button type="button" className="ds-recipient-chip-x" onClick={() => onToggle(s.id)} aria-label={t('dataroom.removeRecipient', { email: s.mail })}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

type ScheduleDatePickerProps = {
  value: string;
  onChange: (value: string) => void;
};

function ScheduleDatePicker({ value, onChange }: ScheduleDatePickerProps) {
  const { t, localeTag } = useUiLocale();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selectedDate = parseDateKey(value);
  const [viewMonth, setViewMonth] = useState(() => selectedDate ?? new Date());
  const cells = useMemo(() => buildMonthCells(viewMonth), [viewMonth]);
  const monthLabel = viewMonth.toLocaleDateString(localeTag, { month: 'long', year: 'numeric' });
  const today = new Date();

  useOutsideClose(open, () => setOpen(false), rootRef);

  const shiftMonth = (delta: number) => {
    setViewMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  };

  return (
    <div className="ds-field-block">
      <span className="ds-field-label">{t('dataroom.date')}</span>
      <div
        className={`ds-datetime-picker ds-datetime-picker--date${open ? ' ds-datetime-picker--open' : ''}`}
        ref={rootRef}
      >
        <button
          type="button"
          className="ds-datetime-field ds-datetime-field--date"
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
        >
          <span className="ds-datetime-icon-wrap" aria-hidden>
            <Calendar size={14} className="ds-datetime-icon" />
          </span>
          <span className="ds-datetime-display">{formatDateLabel(value, t, localeTag)}</span>
          <ChevronDown size={14} className="ds-datetime-chevron" />
        </button>

        {open && (
          <div className="ds-datetime-panel ds-date-panel">
            <div className="ds-date-panel-head">
              <button type="button" className="ds-date-nav-btn" onClick={() => shiftMonth(-1)} aria-label={t('dataroom.previousMonth')}>
                <ChevronLeft size={16} />
              </button>
              <strong>{monthLabel}</strong>
              <button type="button" className="ds-date-nav-btn" onClick={() => shiftMonth(1)} aria-label={t('dataroom.nextMonth')}>
                <ChevronRight size={16} />
              </button>
            </div>
            <div className="ds-date-weekdays">
              {WEEKDAY_KEYS.map((dayKey) => (
                <span key={dayKey}>{t(dayKey)}</span>
              ))}
            </div>
            <div className="ds-date-grid">
              {cells.map((date, index) => {
                if (!date) return <span key={`empty-${index}`} className="ds-date-empty" />;
                const key = toDateKey(date);
                const selected = selectedDate ? isSameDay(date, selectedDate) : false;
                const isToday = isSameDay(date, today);
                return (
                  <button
                    key={key}
                    type="button"
                    className={`ds-date-cell${selected ? ' selected' : ''}${isToday ? ' today' : ''}`}
                    onClick={() => {
                      onChange(key);
                      setOpen(false);
                    }}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>
            <div className="ds-datetime-panel-foot">
              <button type="button" className="ds-datetime-foot-btn" onClick={() => { onChange(''); setOpen(false); }}>
                {t('dataroom.clear')}
              </button>
              <button
                type="button"
                className="ds-datetime-foot-btn ds-datetime-foot-btn--primary"
                onClick={() => {
                  onChange(toDateKey(today));
                  setOpen(false);
                }}
              >
                {t('common.today')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

type ScheduleTimePickerProps = {
  value: string;
  onChange: (value: string) => void;
};

function ScheduleTimePicker({ value, onChange }: ScheduleTimePickerProps) {
  const { t, localeTag } = useUiLocale();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const [hour, minute] = value ? value.split(':') : ['09', '00'];

  useOutsideClose(open, () => setOpen(false), rootRef);

  const selectHour = (nextHour: string) => {
    onChange(`${nextHour}:${minute || '00'}`);
  };

  const selectMinute = (nextMinute: string) => {
    onChange(`${hour || '09'}:${nextMinute}`);
    setOpen(false);
  };

  const selectNow = () => {
    const now = new Date();
    onChange(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
    setOpen(false);
  };

  return (
    <div className="ds-field-block">
      <span className="ds-field-label">{t('dataroom.time')}</span>
      <div
        className={`ds-datetime-picker ds-datetime-picker--time${open ? ' ds-datetime-picker--open' : ''}`}
        ref={rootRef}
      >
        <button
          type="button"
          className="ds-datetime-field ds-datetime-field--time"
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
        >
          <span className="ds-datetime-icon-wrap ds-datetime-icon-wrap--time" aria-hidden>
            <Clock size={14} className="ds-datetime-icon" />
          </span>
          <span className="ds-datetime-display">{formatTimeLabel(value, t, localeTag)}</span>
          <ChevronDown size={14} className="ds-datetime-chevron" />
        </button>

        {open && (
          <div className="ds-datetime-panel ds-time-panel">
            <div className="ds-time-columns hawk-scrollbar-neon-blue">
              <div className="ds-time-column hawk-scrollbar-neon-blue">
                <span className="ds-time-column-label">{t('dataroom.hour')}</span>
                {HOUR_OPTIONS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`ds-time-option${hour === option ? ' selected' : ''}`}
                    onClick={() => selectHour(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
              <div className="ds-time-column hawk-scrollbar-neon-blue">
                <span className="ds-time-column-label">{t('dataroom.minute')}</span>
                {MINUTE_OPTIONS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`ds-time-option${minute === option ? ' selected' : ''}`}
                    onClick={() => selectMinute(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
            <div className="ds-datetime-panel-foot">
              <button type="button" className="ds-datetime-foot-btn ds-datetime-foot-btn--primary" onClick={selectNow}>
                {t('dataroom.now')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

type TimezoneSelectProps = {
  value: string;
  onChange: (value: string) => void;
};

function TimezoneSelect({ value, onChange }: TimezoneSelectProps) {
  const { t } = useUiLocale();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = SCHEDULE_TIMEZONE_CONFIG.find((option) => option.value === value)
    ?? SCHEDULE_TIMEZONE_CONFIG[1];

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="ds-field-block">
      <label className="ds-field-label">{t('dataroom.timezone')}</label>
      <div className={`ds-combobox ds-timezone-select${open ? ' ds-combobox--open' : ''}`} ref={rootRef}>
        <button
          type="button"
          className="ds-combobox-trigger ds-timezone-trigger"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <LanguageFlag countryCode={selected.countryCode} className="ds-timezone-flag" />
          <span className="ds-combobox-placeholder ds-timezone-value">{t(selected.labelKey)}</span>
          <ChevronDown size={14} className="ds-combobox-chevron" />
        </button>

        {open && (
          <div className="ds-combobox-panel">
            {SCHEDULE_TIMEZONE_CONFIG.map((option) => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`ds-timezone-option${isSelected ? ' selected' : ''}`}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  <LanguageFlag countryCode={option.countryCode} className="ds-timezone-flag" />
                  <span>{t(option.labelKey)}</span>
                  {isSelected && <Check size={14} className="ds-timezone-check" aria-hidden />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function articleMatchesSearch(article: DataroomArticle, query: string, t: TFunc): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    article.title,
    article.url,
    article.summary,
    ...article.topics,
    ...article.companies,
    ...articleTopicLabels(article, t),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

function DataroomLibraryPanel({
  articles,
  topics,
  onRemoveArticle,
  selectedArticleIds,
  onToggleArticleSelection,
  onDragArticleStart,
}: {
  articles: DataroomArticle[];
  topics: string[];
  onRemoveArticle: (articleId: number) => void;
  selectedArticleIds: Set<number>;
  onToggleArticleSelection: (articleId: number) => void;
  onDragArticleStart: (event: DragEvent<HTMLElement>, articleId: number) => void;
}) {
  const { t } = useUiLocale();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredArticles = useMemo(
    () => articles.filter((article) => articleMatchesSearch(article, searchQuery, t)),
    [articles, searchQuery, t],
  );

  return (
    <aside className="ds-side-panel ds-library-panel" aria-label={t('dataroom.libraryAriaLabel')}>
      <div className="ds-library-context">
        <div className="ds-crumbs ds-crumbs--side">
          <Link to="/collected-raw">{t('dataroom.breadcrumbDataRooms')}</Link>
          <span className="sep">{'\u203A'}</span>
          <span>{t('dataroom.pageTitle')}</span>
        </div>
        <h1 className="ds-library-context-title">{t('dataroom.deliveryPublishingTitle')}</h1>
        <p className="ds-library-context-sub">
          {t('dataroom.deliveryPublishingSubtitle')}
        </p>
      </div>

      <div className="ds-side-head">
        <div>
          <h2 className="ds-side-title">{t('dataroom.dataroomTitle')}</h2>
          <p className="ds-side-sub">
            {articles.length === 1
              ? t('dataroom.savedArticles', { count: articles.length })
              : t('dataroom.savedArticlesPlural', { count: articles.length })}
          </p>
        </div>
        <span className="ds-side-count">{articles.length}</span>
      </div>

      <div className="ds-side-search">
        <Search size={14} aria-hidden />
        <input
          type="text"
          className="ds-side-search-input"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder={t('dataroom.searchDataroom')}
          aria-label={t('dataroom.searchDataroom')}
        />
        {searchQuery.trim() ? (
          <button
            type="button"
            className="ds-side-search-clear"
            onClick={() => setSearchQuery('')}
            aria-label={t('common.clearFilters')}
          >
            <X size={13} />
          </button>
        ) : null}
      </div>

      <div className="ds-topic-strip" aria-label={t('dataroom.collectedTopicsAria')}>
        {topics.slice(0, 5).map((topic) => (
          <span key={topic} className={`ds-topic-tag ds-topic-tag--${topicTagTone(topic)}`}>
            {topic}
          </span>
        ))}
        {topics.length === 0 && <span className="ds-topic-tag ds-topic-tag--default">{t('dataroom.noTopicsYet')}</span>}
      </div>

      <div className="ds-side-list hawk-scrollbar-neon-blue">
        {articles.length === 0 ? (
          <p className="ds-side-empty">{t('dataroom.collectedArticlesEmpty')}</p>
        ) : filteredArticles.length === 0 ? (
          <p className="ds-side-empty">{t('dataroom.searchNoResults')}</p>
        ) : (
          filteredArticles.map((article) => (
            <article
              key={article.articleId}
              className={`ds-side-article${selectedArticleIds.has(article.articleId) ? ' selected' : ''}`}
              draggable
              onDragStart={(event) => onDragArticleStart(event, article.articleId)}
              onClick={() => onToggleArticleSelection(article.articleId)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onToggleArticleSelection(article.articleId);
                }
              }}
            >
              <div className="ds-side-article-icon">
                <FileText size={15} />
              </div>
              <div className="ds-side-article-main">
                <h3>{article.title}</h3>
                <div className="ds-side-article-tags">
                  <TopicTagsOverflow
                    topics={articleTopicLabels(article, t)}
                    maxVisible={2}
                    moreLabel={(count) => t('dataPool.moreTopics', { count })}
                    classNameForTopic={dataroomTopicTagClass}
                    className="relative min-w-0"
                    moreButtonClassName="ds-topic-tag ds-topic-tag--default shrink-0 cursor-pointer hover:opacity-90"
                    popoverClassName="absolute left-0 top-[calc(100%+4px)] z-30 min-w-[9rem] max-w-[14rem] rounded-lg border border-slate-200 bg-white p-2 shadow-lg"
                  />
                </div>
              </div>
              <button
                type="button"
                className="ds-side-article-remove"
                onClick={(event) => {
                  event.stopPropagation();
                  onRemoveArticle(article.articleId);
                }}
                aria-label={t('dataroom.removeArticleFromDataroom', { title: article.title })}
              >
                <X size={13} />
              </button>
            </article>
          ))
        )}
      </div>
    </aside>
  );
}

function DataroomAssistantPanel({
  articles,
  onError,
  onSuccess,
}: {
  articles: DataroomArticle[];
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}) {
  const { t } = useUiLocale();
  const chatStreamRef = useRef<HTMLDivElement>(null);
  const messageIdRef = useRef(1);
  const sessionIdRef = useRef<number | null>(null);
  const [chatInput, setChatInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isDropActive, setIsDropActive] = useState(false);
  const [attachedArticles, setAttachedArticles] = useState<AttachedChatArticle[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([
    { id: 'welcome', role: 'assistant', text: t(ASSISTANT_WELCOME_KEY) },
  ]);

  useEffect(() => {
    const node = chatStreamRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [messages, isSending, isImporting, attachedArticles]);

  const pushAssistantMessage = (text: string) => {
    setMessages((prev) => [
      ...prev,
      { id: `assistant-${messageIdRef.current++}`, role: 'assistant', text },
    ]);
  };

  const handleNewChat = () => {
    sessionIdRef.current = null;
    messageIdRef.current = 1;
    setChatInput('');
    setAttachedArticles([]);
    setIsDropActive(false);
    setMessages([{ id: 'welcome', role: 'assistant', text: t(ASSISTANT_WELCOME_KEY) }]);
  };

  const handleImportArticle = async (article: DataroomArticle) => {
    if (isImporting || isSending) return;
    if (attachedArticles.some((item) => item.rawDataId === article.articleId)) {
      onError(t('dataroom.assistantAlreadyImported', { title: article.title }));
      return;
    }

    setIsImporting(true);
    try {
      const sessionId = await ensureChatSession(sessionIdRef.current);
      sessionIdRef.current = sessionId;
      const attached = await importDroppedArticle(sessionId, article);
      setAttachedArticles((prev) => [...prev, attached]);
      pushAssistantMessage(
        t('dataroom.assistantImportedArticle', {
          title: attached.title,
          chunks: attached.chunkCount ?? 0,
        }),
      );
      onSuccess(t('dataroom.assistantImportSuccess', { title: attached.title }));
    } catch (err: unknown) {
      const message = formatChatError(err, t('dataroom.assistantImportFailed'));
      onError(message);
      pushAssistantMessage(message);
    } finally {
      setIsImporting(false);
      setIsDropActive(false);
    }
  };

  const handleAssistantDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsDropActive(false);
    const articleId = readDroppedArticleId(event.dataTransfer);
    if (articleId == null) return;
    const article = articles.find((item) => item.articleId === articleId);
    if (!article) {
      onError(t('dataroom.assistantDropUnknownArticle'));
      return;
    }
    void handleImportArticle(article);
  };

  const handleSend = async () => {
    const trimmed = chatInput.trim();
    if (!trimmed || isSending || isImporting) return;

    const userMessage: ChatMessage = {
      id: `user-${messageIdRef.current++}`,
      role: 'user',
      text: trimmed,
    };

    setMessages((prev) => [...prev, userMessage]);
    setChatInput('');
    setIsSending(true);

    try {
      const sessionId = await ensureChatSession(sessionIdRef.current);
      sessionIdRef.current = sessionId;
      const reply = await askChatbot(sessionId, trimmed);
      pushAssistantMessage(reply);
    } catch (err: unknown) {
      const message = formatChatError(err, t('dataroom.assistantRequestFailed'));
      onError(message);
      pushAssistantMessage(message);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <aside
      className={`ds-side-panel ds-assistant-panel${isDropActive ? ' ds-assistant-panel--drop-active' : ''}`}
      aria-label={t('dataroom.assistantAriaLabel')}
      onDragEnter={(event) => {
        event.preventDefault();
        setIsDropActive(true);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        setIsDropActive(true);
      }}
      onDragLeave={(event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node)) return;
        setIsDropActive(false);
      }}
      onDrop={handleAssistantDrop}
    >
      <div className="ds-assistant-head">
        <div className="ds-assistant-title-wrap">
          <Sparkles size={15} />
          <h2 className="ds-side-title">{t('dataroom.aiContentAssistant')}</h2>
        </div>
        <button type="button" className="ds-dispatch-link" onClick={handleNewChat}>
          {t('dataroom.newChat')}
        </button>
      </div>

      <p className="ds-assistant-drop-hint">{t('dataroom.assistantDropHint')}</p>

      {attachedArticles.length > 0 ? (
        <div className="ds-assistant-attachments" aria-label={t('dataroom.assistantAttachedAria')}>
          {attachedArticles.map((article) => (
            <span key={article.rawDataId} className="ds-assistant-attach-chip" title={article.title}>
              <FileText size={12} aria-hidden />
              <span>{article.title}</span>
            </span>
          ))}
        </div>
      ) : null}

      <div ref={chatStreamRef} className="ds-chat-stream hawk-scrollbar-neon-blue">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`ds-chat-bubble${message.role === 'user' ? ' ds-chat-bubble--user' : ''}`}
          >
            <p style={{ whiteSpace: 'pre-wrap' }}>{message.text}</p>
          </div>
        ))}
        {(isSending || isImporting) && (
          <div className="ds-chat-bubble ds-chat-bubble--pending" aria-live="polite">
            <p>{isImporting ? t('dataroom.assistantImporting') : t('dataroom.thinking')}</p>
          </div>
        )}
      </div>

      <div className="ds-chat-input">
        <input
          type="text"
          placeholder={t('dataroom.messagePlaceholder')}
          aria-label={t('dataroom.messageAriaLabel')}
          value={chatInput}
          onChange={(event) => setChatInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void handleSend();
            }
          }}
          disabled={isSending || isImporting}
        />
        <button
          type="button"
          aria-label={t('dataroom.sendMessageAria')}
          disabled={isSending || isImporting || !chatInput.trim()}
          onClick={() => void handleSend()}
        >
          <Send size={14} />
        </button>
      </div>
    </aside>
  );
}

export function DataroomSettingPage() {
  const { articles, removeArticle } = useDataroom();
  const { showSuccess, showError } = useToast() as ToastApi;
  const { t, localeTag } = useUiLocale();

  const [length, setLength]           = useState<Length>('Standard');
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>('send_now');
  const [autoSummarise, setAutoSum]   = useState(true);
  const [includeQuotes, setQuotes]    = useState(true);
  const [generatedPreview, setGeneratedPreview] = useState<GeneratedPreview | null>(null);
  const [generatedHtml, setGeneratedHtml] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [fullPreviewOpen, setFullPreviewOpen] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>('pc');

  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [isLoadingSubscribers, setIsLoadingSubscribers] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [emailSubject, setEmailSubject] = useState(() => defaultEmailSubject(localeTag, t));
  const [isSending, setIsSending] = useState(false);

  const [scheduleType, setScheduleType] = useState<ScheduleType>('one_time');
  const [scheduleDate, setScheduleDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [scheduleTimezone, setScheduleTimezone] = useState('Europe/Berlin');
  const [isScheduling, setIsScheduling] = useState(false);

  const [exportFormat, setExportFormat] = useState<ExportFormat>('PDF');
  const [exportIncludeSummary, setExportIncludeSummary] = useState(true);
  const [exportIncludeLinks, setExportIncludeLinks] = useState(true);
  const [exportIncludeTopicGrouping, setExportIncludeTopicGrouping] = useState(true);
  const [exportRawUrlsOnly, setExportRawUrlsOnly] = useState(false);
  const [exportFileName, setExportFileName] = useState(defaultExportFileName);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedArticleIds, setSelectedArticleIds] = useState<Set<number>>(new Set());

  const topics = useMemo(
    () => dedupeStrings(articles.flatMap((a) => a.topics)),
    [articles],
  );
  useEffect(() => {
    if (deliveryMethod !== 'send_now' && deliveryMethod !== 'schedule_send') return;
    setIsLoadingSubscribers(true);
    subscribersApi.list({ limit: 200 })
      .then((res: { data: Subscriber[] | { items?: Subscriber[] } }) => {
        const data = res.data;
        const list: Subscriber[] = Array.isArray(data) ? data : ((data as { items?: Subscriber[] }).items ?? []);
        setSubscribers(list);
      })
      .catch(() => setSubscribers([]))
      .finally(() => setIsLoadingSubscribers(false));
  }, [deliveryMethod]);

  useEffect(() => {
    setSelectedArticleIds((prev) => {
      const next = new Set<number>();
      const availableIds = new Set(articles.map((article) => article.articleId));
      prev.forEach((id) => {
        if (availableIds.has(id)) next.add(id);
      });
      return next;
    });
  }, [articles]);

  const toggleArticleSelection = (articleId: number) => {
    setSelectedArticleIds((prev) => {
      const next = new Set(prev);
      if (next.has(articleId)) next.delete(articleId);
      else next.add(articleId);
      return next;
    });
  };

  const handleClearSelectedArticles = () => {
    setSelectedArticleIds(new Set());
    invalidatePreview();
  };

  const handleArticleDragStart = (event: DragEvent<HTMLElement>, articleId: number) => {
    setArticleDragData(event.dataTransfer, articleId);
  };

  const handlePreviewDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const articleId = Number(event.dataTransfer.getData('text/plain'));
    if (!Number.isNaN(articleId)) {
      setSelectedArticleIds((prev) => {
        if (prev.has(articleId)) return prev;
        const next = new Set(prev);
        next.add(articleId);
        return next;
      });
    }
  };

  const selectedArticles = useMemo(
    () => articles.filter((article) => selectedArticleIds.has(article.articleId)),
    [articles, selectedArticleIds],
  );

  const handleSelectAll = () => setSelectedIds(new Set(subscribers.map((s) => s.id)));
  const handleClearRecipients = () => setSelectedIds(new Set());
  const toggleSubscriber = (id: number) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const invalidatePreview = () => {
    setGeneratedPreview(null);
    setGeneratedHtml(null);
    setGenerateError(null);
  };

  const handleDeliveryMethodChange = (method: DeliveryMethod) => {
    setDeliveryMethod(method);
  };

  const selectedPreviewDevice = PREVIEW_DEVICE_CONFIG.find((device) => device.id === previewDevice) ?? PREVIEW_DEVICE_CONFIG[2];
  const selectedPreviewDeviceLabel = t(selectedPreviewDevice.labelKey);

  const generatedHtmlWithScrollbar = useMemo(() => {
    if (!generatedHtml) return null;
    const scrollbarStyle = `
      <style>
        html {
          scrollbar-width: thin;
          scrollbar-color: #3b82f6 transparent;
        }
        html::-webkit-scrollbar,
        body::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        html::-webkit-scrollbar-button,
        body::-webkit-scrollbar-button {
          display: none;
          width: 0;
          height: 0;
        }
        html::-webkit-scrollbar-track,
        body::-webkit-scrollbar-track {
          background: transparent;
        }
        html::-webkit-scrollbar-thumb,
        body::-webkit-scrollbar-thumb {
          border-radius: 999px;
          background: linear-gradient(180deg, #60a5fa 0%, #2563eb 100%);
          box-shadow: 0 0 8px rgba(37, 99, 235, 0.85);
        }
        body {
          overflow-x: hidden;
        }
      </style>
    `;
    if (/<\/head>/i.test(generatedHtml)) {
      return generatedHtml.replace(/<\/head>/i, `${scrollbarStyle}</head>`);
    }
    return `${scrollbarStyle}${generatedHtml}`;
  }, [generatedHtml]);

  const clearDispatchedArticlesLocally = (dispatched: DataroomArticle[]) => {
    for (const article of dispatched) {
      removeArticle(article.articleId);
    }
    setSelectedArticleIds(new Set());
    invalidatePreview();
  };

  const removeStaleArticlesLocally = (stale: DataroomArticle[]) => {
    if (stale.length === 0) return;
    for (const article of stale) {
      removeArticle(article.articleId);
    }
    setSelectedArticleIds((prev) => {
      const next = new Set(prev);
      stale.forEach((article) => next.delete(article.articleId));
      return next;
    });
    invalidatePreview();
  };

  const handleStaleArticles = (stale: DataroomArticle[]): void => {
    if (stale.length === 0) return;
    removeStaleArticlesLocally(stale);
    const titles = stale.map((article) => `"${article.title}"`).join(', ');
    throw new Error(t('dataroom.errStaleArticles', { count: stale.length, titles }));
  };

  useEffect(() => {
    if (articles.length === 0 || !getModeratorApiKey()) return;
    let cancelled = false;
    findStaleArticlesOnServer(articles).then((stale) => {
      if (cancelled || stale.length === 0) return;
      removeStaleArticlesLocally(stale);
      const titles = stale.map((article) => article.title).join(', ');
      showError(
        t('dataroom.toastStaleArticlesRemoved', { count: stale.length, titles }),
        12000,
      );
    }).catch(() => { /* non-blocking */ });
    return () => { cancelled = true; };
  }, [articles.length]);

  const handleSendEmail = async () => {
    if (selectedIds.size === 0) {
      showError(t('dataroom.toastSelectRecipient'));
      return;
    }
    if (selectedArticles.length === 0) {
      showError(t('dataroom.toastSelectArticle'));
      return;
    }
    if (!generatedHtml) {
      showError(t('dataroom.toastGeneratePreview'));
      return;
    }
    if (!getModeratorApiKey()) {
      showError(t('dataroom.toastApiKeyMissing'));
      return;
    }

    setIsSending(true);
    let successCount = 0;
    let errorCount = 0;
    let lastError = '';
    const dispatchedSnapshot = [...selectedArticles];

    try {
      await dataroomApi.patchSettings({ length });
    } catch {
      // Non-blocking — dispatch still uses server defaults if settings patch fails.
    }

    for (const id of selectedIds) {
      try {
        const { synced, stale } = await syncSelectedArticlesToBackend(dispatchedSnapshot, t);
        if (stale.length > 0) {
          handleStaleArticles(stale);
        }
        if (synced.length === 0) {
          throw new Error(t('dataroom.errNoValidArticles'));
        }
        await dispatchAndVerify({ channel: 'email', subscriber_id: id }, t);
        successCount++;
      } catch (err: unknown) {
        errorCount++;
        lastError = err instanceof Error ? err.message : extractApiError(err);
      }
    }

    setIsSending(false);
    if (errorCount === 0) {
      clearDispatchedArticlesLocally(dispatchedSnapshot);
      showSuccess(
        successCount === 1
          ? t('dataroom.toastSentSuccess', { count: successCount })
          : t('dataroom.toastSentSuccessPlural', { count: successCount }),
      );
    } else if (successCount > 0) {
      clearDispatchedArticlesLocally(dispatchedSnapshot);
      showError(t('dataroom.toastSentPartial', { success: successCount, failed: errorCount, error: lastError }));
    } else {
      showError(lastError || t('dataroom.toastSendFailed'));
    }
  };

  const buildScheduledIso = (): string | null => {
    if (!scheduleDate || !scheduleTime) return null;
    const [hh, mm] = scheduleTime.split(':').map(Number);
    if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
    const local = new Date(`${scheduleDate}T${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:00`);
    if (Number.isNaN(local.getTime())) return null;
    return local.toISOString();
  };

  const handleScheduleSend = async () => {
    const scheduled_for = scheduleType === 'one_time' ? buildScheduledIso() : null;
    if (scheduleType === 'one_time' && !scheduled_for) {
      showError(t('dataroom.toastChooseSchedule'));
      return;
    }
    if (selectedIds.size === 0) {
      showError(t('dataroom.toastSelectRecipient'));
      return;
    }
    if (selectedArticles.length === 0) {
      showError(t('dataroom.toastSelectArticle'));
      return;
    }
    if (!generatedHtml) {
      showError(t('dataroom.toastGenerateBeforeSchedule'));
      return;
    }
    if (!getModeratorApiKey()) {
      showError(t('dataroom.toastApiKeyMissing'));
      return;
    }

    setIsScheduling(true);
    let successCount = 0;
    let errorCount = 0;
    let lastError = '';
    const dispatchedSnapshot = [...selectedArticles];

    try {
      await dataroomApi.patchSettings({ length });
    } catch {
      // Non-blocking.
    }

    for (const id of selectedIds) {
      try {
        const { synced, stale } = await syncSelectedArticlesToBackend(dispatchedSnapshot, t);
        if (stale.length > 0) {
          handleStaleArticles(stale);
        }
        if (synced.length === 0) {
          throw new Error(t('dataroom.errNoValidArticles'));
        }
        await dispatchAndVerify({
          channel: 'schedule',
          subscriber_id: id,
          ...(scheduled_for ? { scheduled_for } : {}),
        }, t);
        successCount++;
      } catch (err: unknown) {
        errorCount++;
        lastError = err instanceof Error ? err.message : extractApiError(err);
      }
    }

    setIsScheduling(false);
    if (errorCount === 0) {
      clearDispatchedArticlesLocally(dispatchedSnapshot);
      showSuccess(
        successCount === 1
          ? t('dataroom.toastScheduledSuccess', { count: successCount })
          : t('dataroom.toastScheduledSuccessPlural', { count: successCount }),
      );
    } else if (successCount > 0) {
      clearDispatchedArticlesLocally(dispatchedSnapshot);
      showError(t('dataroom.toastScheduledPartial', { success: successCount, failed: errorCount, error: lastError }));
    } else {
      showError(lastError || t('dataroom.toastScheduleFailed'));
    }
  };

  const handleExportFile = async () => {
    if (!generatedHtml && articles.length === 0) return;
    setIsExporting(true);
    try {
      const baseName = exportFileName.trim() || defaultExportFileName();
      if (exportFormat === 'CSV') {
        const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
        const rows: string[] = [];
        if (exportIncludeTopicGrouping) {
          const byTopic = new Map<string, typeof articles>();
          for (const a of articles) {
            const keys = a.topics.length ? a.topics : [t('common.uncategorized')];
            for (const t of keys) {
              if (!byTopic.has(t)) byTopic.set(t, []);
              byTopic.get(t)!.push(a);
            }
          }
          rows.push('Topic,Title,URL,Summary');
          for (const [topic, list] of byTopic) {
            for (const a of list) {
              rows.push([
                escape(topic),
                escape(a.title),
                escape(exportRawUrlsOnly ? (a.url ?? '') : (a.url ?? '')),
                escape(exportIncludeSummary ? (a.summary ?? '') : ''),
              ].join(','));
            }
          }
        } else {
          rows.push('Title,URL,Summary,Topics');
          for (const a of articles) {
            rows.push([
              escape(a.title),
              escape(a.url ?? ''),
              escape(exportIncludeSummary ? (a.summary ?? '') : ''),
              escape(a.topics.join('; ')),
            ].join(','));
          }
        }
        const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${baseName}.csv`;
        link.click();
        URL.revokeObjectURL(url);
        showSuccess(t('dataroom.toastCsvDownloaded'));
      } else if (exportFormat === 'PDF') {
        const win = window.open('', '_blank');
        if (win) {
          win.document.write(generatedHtml ?? `<html><body><h1>${emailSubject}</h1></body></html>`);
          win.document.close();
          win.focus();
          win.print();
        }
        showSuccess(t('dataroom.toastPdfPrint'));
      } else {
        const html = generatedHtml ?? `<html><body><h1>${emailSubject}</h1></body></html>`;
        const blob = new Blob([html], { type: 'application/msword' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${baseName}.doc`;
        link.click();
        URL.revokeObjectURL(url);
        showSuccess(t('dataroom.toastDocDownloaded'));
      }
    } catch {
      showError(t('dataroom.toastExportFailed'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleSaveDraft = () => {
    try {
      const draft = {
        deliveryMethod,
        emailSubject,
        selectedIds: [...selectedIds],
        scheduleType,
        scheduleDate,
        scheduleTime,
        scheduleTimezone,
        exportFormat,
        exportFileName,
        length,
        autoSummarise,
        includeQuotes,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem('hawk_dataroom_delivery_draft', JSON.stringify(draft));
      showSuccess(t('dataroom.toastDraftSaved'));
    } catch {
      showError(t('dataroom.toastDraftSaveFailed'));
    }
  };

  const generatePreview = async () => {
    if (selectedArticles.length === 0) return;
    setIsGenerating(true);
    setGenerateError(null);
    try {
      const payload = {
        articles: selectedArticles.map((a) => ({
          title: a.title,
          summary: autoSummarise ? (a.summary || undefined) : undefined,
          url: a.url || undefined,
          topics: a.topics,
          companies: a.companies,
        })),
        length,
        include_quotes: includeQuotes,
        auto_summarise: autoSummarise,
      };
      const res = await dataroomApi.generateEmailPreview(payload);
      const now = new Date();
      setGeneratedHtml(res.data.html);
      setGeneratedPreview({
        generatedAt: now,
        generatedAtLabel: formatGeneratedTime(now, localeTag),
        subject: emailSubject,
        articleCount: selectedArticles.length,
        topics: selectedTopics.slice(0, 6),
        recipientCount: selectedIds.size,
      });
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ??
        (err as { message?: string })?.message ??
        t('dataroom.errGenerationFailed');
      setGenerateError(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const selectedTopics = useMemo(
    () => dedupeStrings(selectedArticles.flatMap((a) => a.topics)),
    [selectedArticles],
  );

  const previewGroupedArticles = useMemo(() => {
    const map = new Map<string, typeof selectedArticles>();
    for (const a of selectedArticles) {
      const keys = a.topics.length ? a.topics : [t('dataroom.general')];
      for (const t of keys) {
        if (!map.has(t)) map.set(t, []);
        if (!map.get(t)!.some((x) => x.articleId === a.articleId)) map.get(t)!.push(a);
      }
    }
    return [...map.entries()];
  }, [selectedArticles, t]);

  const hasPreview = Boolean(generatedHtml && generatedPreview);

  const validationMessage = useMemo(() => {
    if (articles.length === 0) return t('dataroom.validationNoArticles');
    if (selectedArticles.length === 0) return t('dataroom.validationNoSelected');
    if (!hasPreview) {
      if (generateError) return generateError;
      return t('dataroom.validationGeneratePreview');
    }
    if (deliveryMethod === 'schedule_send' && scheduleType === 'one_time' && (!scheduleDate || !scheduleTime)) {
      return t('dataroom.validationScheduleDateTime');
    }
    return '';
  }, [articles.length, selectedArticles.length, hasPreview, deliveryMethod, scheduleType, scheduleDate, scheduleTime, generateError, t]);

  const showValidationWarn = articles.length === 0 || !hasPreview || Boolean(validationMessage);

  const finalActionDisabled = useMemo(() => {
    if (!hasPreview || selectedArticles.length === 0 || isGenerating) return true;
    if (deliveryMethod === 'send_now') return selectedIds.size === 0 || isSending;
    if (deliveryMethod === 'schedule_send') {
      if (scheduleType === 'one_time') {
        return selectedIds.size === 0 || !scheduleDate || !scheduleTime || isScheduling;
      }
      return selectedIds.size === 0 || isScheduling;
    }
    return isExporting;
  }, [hasPreview, selectedArticles.length, isGenerating, deliveryMethod, scheduleType, selectedIds.size, scheduleDate, scheduleTime, isSending, isScheduling, isExporting]);

  const finalActionLabel =
    deliveryMethod === 'send_now'
      ? t('dataroom.sendEmailNow')
      : deliveryMethod === 'schedule_send'
        ? t('dataroom.scheduleSend')
        : t('dataroom.downloadFile');

  const handleFinalAction = () => {
    if (deliveryMethod === 'send_now') void handleSendEmail();
    else if (deliveryMethod === 'schedule_send') void handleScheduleSend();
    else void handleExportFile();
  };

  return (
    <div className="dataroom-setting-page flex min-h-0 flex-1 flex-col">
      <div className="ds-page-shell hawk-scrollbar-neon-blue">
        <DataroomLibraryPanel
          articles={articles}
          topics={topics}
          onRemoveArticle={removeArticle}
          selectedArticleIds={selectedArticleIds}
          onToggleArticleSelection={toggleArticleSelection}
          onDragArticleStart={handleArticleDragStart}
        />

        <main className="ds-main">
        {/* Delivery & Publishing */}
        <section className="ds-card ds-delivery-card">
          <div className="ds-delivery-columns hawk-scrollbar-neon-blue">
            <div className="ds-delivery-action-bar">
              <div className="ds-delivery-actions">
                <button type="button" className="ds-btn ds-btn-ghost" onClick={handleSaveDraft}>
                  <Save size={14} />
                  {t('dataroom.saveDraft')}
                </button>
                <button
                  type="button"
                  className="ds-btn ds-btn-primary ds-btn-final"
                  disabled={finalActionDisabled}
                  onClick={handleFinalAction}
                >
                  {deliveryMethod === 'send_now' && <Send size={14} />}
                  {deliveryMethod === 'schedule_send' && <CalendarClock size={14} />}
                  {deliveryMethod === 'export_file' && <Download size={14} />}
                  {isSending || isScheduling || isExporting ? t('dataroom.working') : finalActionLabel}
                </button>
              </div>
            </div>

            {/* Center — Configuration */}
            <div className="ds-delivery-config">
              <h4 className="ds-delivery-panel-title">{t('dataroom.configuration')}</h4>

              {deliveryMethod === 'send_now' && (
                <div className="ds-config-stack hawk-scrollbar-neon-blue">
                  <SubscriberMultiSelect
                    subscribers={subscribers}
                    selectedIds={selectedIds}
                    isLoading={isLoadingSubscribers}
                    onToggle={toggleSubscriber}
                    onSelectAll={handleSelectAll}
                    onClear={handleClearRecipients}
                  />
                  <div className="ds-field-block">
                    <label className="ds-field-label" htmlFor="ds-email-subject">{t('dataroom.emailSubject')}</label>
                    <input
                      id="ds-email-subject"
                      className="ds-text-input"
                      value={emailSubject}
                      onChange={(e) => { setEmailSubject(e.target.value); invalidatePreview(); }}
                    />
                  </div>
                  <div className="ds-info-box">
                    {t('dataroom.sendNowInfo')}
                  </div>
                </div>
              )}

              {deliveryMethod === 'schedule_send' && (
                <div className="ds-config-stack hawk-scrollbar-neon-blue">
                  <div className="ds-field-block">
                    <label className="ds-field-label">{t('dataroom.scheduleType')}</label>
                    <div className="ds-segmented">
                      <button
                        type="button"
                        className={`ds-segmented-btn${scheduleType === 'one_time' ? ' on' : ''}`}
                        onClick={() => setScheduleType('one_time')}
                      >
                        {t('dataroom.oneTime')}
                      </button>
                      <button
                        type="button"
                        className={`ds-segmented-btn${scheduleType === 'recurring' ? ' on' : ''}`}
                        onClick={() => setScheduleType('recurring')}
                      >
                        {t('dataroom.recurring')}
                      </button>
                    </div>
                  </div>
                  <div className="ds-field-grid">
                    <ScheduleDatePicker value={scheduleDate} onChange={setScheduleDate} />
                    <ScheduleTimePicker value={scheduleTime} onChange={setScheduleTime} />
                  </div>
                  <TimezoneSelect
                    value={scheduleTimezone}
                    onChange={setScheduleTimezone}
                  />
                  <SubscriberMultiSelect
                    subscribers={subscribers}
                    selectedIds={selectedIds}
                    isLoading={isLoadingSubscribers}
                    onToggle={toggleSubscriber}
                    onSelectAll={handleSelectAll}
                    onClear={handleClearRecipients}
                  />
                  <div className="ds-field-block">
                    <label className="ds-field-label" htmlFor="ds-schedule-subject">{t('dataroom.emailSubject')}</label>
                    <input
                      id="ds-schedule-subject"
                      className="ds-text-input"
                      value={emailSubject}
                      onChange={(e) => { setEmailSubject(e.target.value); invalidatePreview(); }}
                    />
                  </div>
                  {scheduleType === 'recurring' && (
                    <div className="ds-info-box ds-info-box--muted">
                      {t('dataroom.recurringInfo')}
                    </div>
                  )}
                </div>
              )}

              {deliveryMethod === 'export_file' && (
                <div className="ds-config-stack hawk-scrollbar-neon-blue">
                  <div className="ds-field-block">
                    <label className="ds-field-label">{t('dataroom.exportFormat')}</label>
                    <div className="ds-segmented">
                      {(['PDF', 'DOCX', 'CSV'] as ExportFormat[]).map((fmt) => (
                        <button
                          key={fmt}
                          type="button"
                          className={`ds-segmented-btn${exportFormat === fmt ? ' on' : ''}`}
                          onClick={() => setExportFormat(fmt)}
                        >
                          {fmt}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="ds-field-block">
                    <label className="ds-field-label">{t('dataroom.fileContent')}</label>
                    <div className="ds-check-list">
                      <label className="ds-check-row">
                        <input type="checkbox" checked={exportIncludeSummary} onChange={(e) => setExportIncludeSummary(e.target.checked)} />
                        {t('dataroom.includeArticleSummary')}
                      </label>
                      <label className="ds-check-row">
                        <input type="checkbox" checked={exportIncludeLinks} onChange={(e) => setExportIncludeLinks(e.target.checked)} />
                        {t('dataroom.includeSourceLinks')}
                      </label>
                      <label className="ds-check-row">
                        <input type="checkbox" checked={exportIncludeTopicGrouping} onChange={(e) => setExportIncludeTopicGrouping(e.target.checked)} />
                        {t('dataroom.includeTopicGrouping')}
                      </label>
                      <label className="ds-check-row">
                        <input type="checkbox" checked={exportRawUrlsOnly} onChange={(e) => setExportRawUrlsOnly(e.target.checked)} />
                        {t('dataroom.includeRawUrlsOnly')}
                      </label>
                    </div>
                  </div>
                  <div className="ds-field-block">
                    <label className="ds-field-label" htmlFor="ds-export-name">{t('dataroom.fileName')}</label>
                    <input
                      id="ds-export-name"
                      className="ds-text-input"
                      value={exportFileName}
                      onChange={(e) => setExportFileName(e.target.value)}
                    />
                  </div>
                </div>
              )}

              <div className="ds-delivery-method-panel" aria-label={t('dataroom.deliveryMethodAria')}>
                <div className="ds-delivery-methods">
                  {DELIVERY_METHOD_CONFIG.map((method) => {
                    const selected = deliveryMethod === method.id;
                    const Icon = method.icon;
                    return (
                      <button
                        key={method.id}
                        type="button"
                        className={`ds-delivery-method${selected ? ' selected' : ''}`}
                        onClick={() => handleDeliveryMethodChange(method.id)}
                        title={t(method.descKey)}
                      >
                        <div className="ds-delivery-method-icon" style={{ background: method.iconBg, color: method.iconColor }}>
                          <Icon size={18} strokeWidth={2} />
                        </div>
                        <div className="ds-delivery-method-label">{t(method.labelKey)}</div>
                        {selected && (
                          <span className="ds-delivery-method-check" aria-hidden>
                            <Check size={12} strokeWidth={3} />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right — Preview & Validation */}
            <div
              className="ds-delivery-preview"
              onDragOver={(event) => event.preventDefault()}
              onDrop={handlePreviewDrop}
            >
              <div className="ds-preview-panel-head">
                <h4 className="ds-delivery-panel-title">{t('dataroom.previewValidation')}</h4>
                {selectedArticles.length > 0 && !hasPreview ? (
                  <div className="ds-preview-action-buttons">
                    <button
                      type="button"
                      className="ds-btn ds-btn-warning ds-preview-clear-btn"
                      disabled={isGenerating}
                      onClick={handleClearSelectedArticles}
                    >
                      {t('dataroom.clear')}
                    </button>
                    <button
                      type="button"
                      className="ds-btn ds-btn-primary ds-preview-generate-btn"
                      disabled={isGenerating}
                      onClick={() => void generatePreview()}
                    >
                      <Sparkles size={14} />
                      {isGenerating ? t('dataroom.generating') : t('dataroom.generate')}
                    </button>
                  </div>
                ) : hasPreview ? (
                  <div className="ds-preview-generated-badge ds-preview-generated-badge--action ds-preview-head-action">
                    <Check size={12} />
                    <span>{t('dataroom.previewGenerated')}</span>
                    <button
                      type="button"
                      className="ds-btn ds-btn-warning ds-preview-clear-btn"
                      disabled={isGenerating}
                      onClick={handleClearSelectedArticles}
                    >
                      {t('dataroom.clear')}
                    </button>
                    <button
                      type="button"
                      className="ds-btn ds-btn-primary ds-preview-regenerate-btn"
                      disabled={isGenerating}
                      onClick={() => void generatePreview()}
                    >
                      {isGenerating ? t('dataroom.regenerating') : t('dataroom.regenerate')}
                    </button>
                  </div>
                ) : showValidationWarn && validationMessage ? (
                  <div className="ds-preview-validation warn">
                    <AlertTriangle size={14} className="ds-validation-icon" />
                    <span>{validationMessage}</span>
                  </div>
                ) : null}
              </div>

              <div className="ds-preview-panel-body hawk-scrollbar-neon-blue">
                {!hasPreview && !isGenerating && (
                  <div className="ds-preview-selection-zone">
                    <p className="ds-preview-selection-title">
                      {t('dataroom.selectedArticles', { count: selectedArticles.length })}
                    </p>
                    <p className="ds-preview-selection-hint">{t('dataroom.selectionHint')}</p>
                    <div className="ds-preview-selection-list">
                      {selectedArticles.length === 0 ? (
                        <span className="ds-preview-selection-empty">{t('dataroom.noArticlesSelected')}</span>
                      ) : (
                        selectedArticles.map((article) => (
                          <button
                            key={article.articleId}
                            type="button"
                            className="ds-preview-selection-item"
                            onClick={() => toggleArticleSelection(article.articleId)}
                            title={t('dataroom.deselectArticle')}
                          >
                            {article.title}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {generateError && !hasPreview ? (
                  <div className="ds-preview-empty-state">
                    <p className="ds-preview-error">{generateError}</p>
                    <button type="button" className="ds-btn ds-btn-primary" onClick={() => void generatePreview()}>
                      {t('common.retry')}
                    </button>
                  </div>
                ) : !hasPreview && isGenerating ? (
                  <div className="ds-preview-empty-state">
                    <svg className="ds-spinner" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                    </svg>
                    <p>{t('dataroom.generatingPreviewAi')}</p>
                  </div>
                ) : hasPreview ? (
                  <div className="ds-preview-generated">
                  {isGenerating && (
                    <div className="ds-preview-regenerating-overlay" aria-live="polite">
                      <svg className="ds-spinner" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                      </svg>
                      <p>{t('dataroom.regeneratingPreview')}</p>
                    </div>
                  )}
                  {generateError && (
                    <div className="ds-preview-regenerate-error">{generateError}</div>
                  )}
                  <div className="ds-preview-stats-row">
                    <div className="ds-preview-stat-h">
                      <div className="ds-preview-stat-icon" style={{ background: '#eff6ff', color: '#2563eb' }}>
                        <Clock size={14} />
                      </div>
                      <div>
                        <span className="ds-preview-stat-label">{t('dataroom.generatedAt')}</span>
                        <span className="ds-preview-stat-value">{generatedPreview!.generatedAtLabel}</span>
                      </div>
                    </div>
                    <div className="ds-preview-stat-h">
                      <div className="ds-preview-stat-icon" style={{ background: '#f0fdf4', color: '#16a34a' }}>
                        <FileText size={14} />
                      </div>
                      <div>
                        <span className="ds-preview-stat-label">{t('dataroom.articlesIncluded')}</span>
                        <span className="ds-preview-stat-value">{t('dataroom.articlesCount', { count: generatedPreview!.articleCount })}</span>
                      </div>
                    </div>
                    <div className="ds-preview-stat-h">
                      <div className="ds-preview-stat-icon" style={{ background: '#faf5ff', color: '#7c3aed' }}>
                        <Hash size={14} />
                      </div>
                      <div>
                        <span className="ds-preview-stat-label">{t('dataroom.topics')}</span>
                        <span className="ds-preview-stat-value">{generatedPreview!.topics.join(', ') || '—'}</span>
                      </div>
                    </div>
                    {(deliveryMethod === 'send_now' || deliveryMethod === 'schedule_send') && (
                      <div className="ds-preview-stat-h">
                        <div className="ds-preview-stat-icon" style={{ background: '#fef3c7', color: '#d97706' }}>
                          <Users size={14} />
                        </div>
                        <div>
                          <span className="ds-preview-stat-label">{t('dataroom.recipients')}</span>
                          <span className="ds-preview-stat-value">{t('dataroom.recipientsCount', { count: selectedIds.size })}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="ds-info-box">{t('dataroom.reviewBeforeSend')}</div>

                  <div className="ds-preview-card">
                    <div className="ds-preview-card-subject">
                      <span className="ds-preview-card-label">{t('dataroom.subject')}</span>
                      <strong>{generatedPreview!.subject}</strong>
                    </div>
                    <div className="ds-preview-card-body">
                      {previewGroupedArticles.map(([topic, list]) => (
                        <div key={topic} className="ds-preview-topic-group">
                          <div className="ds-preview-topic-title">{topic}</div>
                          <ul>
                            {list.slice(0, 4).map((a) => (
                              <li key={a.articleId}>{a.title}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="ds-btn ds-btn-ghost ds-open-preview-btn"
                    onClick={() => setFullPreviewOpen(true)}
                  >
                    <ExternalLink size={14} />
                    {t('dataroom.openFullPreview')}
                  </button>
                </div>
                ) : null}
              </div>
            </div>
          </div>

        </section>

        {fullPreviewOpen && generatedHtmlWithScrollbar && (
          <div className="ds-full-preview-modal">
            <div className="ds-full-preview-backdrop" onClick={() => setFullPreviewOpen(false)} aria-hidden />
            <div className="ds-full-preview-dialog" role="dialog" aria-label={t('dataroom.fullPreviewAria')}>
              <div className="ds-full-preview-head">
                <strong>{emailSubject}</strong>
                <div className="ds-preview-device-toggle" aria-label={t('dataroom.previewDeviceSizeAria')}>
                  {PREVIEW_DEVICE_CONFIG.map((device) => (
                    <button
                      key={device.id}
                      type="button"
                      className={`ds-preview-device-btn${previewDevice === device.id ? ' on' : ''}`}
                      onClick={() => setPreviewDevice(device.id)}
                    >
                      {t(device.labelKey)}
                    </button>
                  ))}
                </div>
                <button type="button" className="ds-icon-btn" onClick={() => setFullPreviewOpen(false)} aria-label={t('common.close')}>
                  <X size={16} />
                </button>
              </div>
              <div className="ds-preview-device-stage">
                <iframe
                  className="ds-preview-iframe ds-preview-iframe--modal"
                  srcDoc={generatedHtmlWithScrollbar}
                  title={t('dataroom.fullDigestPreview', { device: selectedPreviewDeviceLabel })}
                  sandbox="allow-same-origin"
                  style={{
                    width: selectedPreviewDevice.width,
                    height: selectedPreviewDevice.height,
                  }}
                />
              </div>
            </div>
          </div>
        )}

        </main>

        <DataroomAssistantPanel
          articles={articles}
          onError={showError}
          onSuccess={showSuccess}
        />
      </div>
    </div>
  );
}
